/**
 * The identity-strip selection, kept across reloads.
 *
 * ## Why this exists
 *
 * `activeTenantId` and `activeCompanyId` are client state — the server returns
 * the lists, but has no opinion about which row this browser is looking at — and
 * they used to live ONLY in Redux memory. So every reload started at `null`, the
 * reconciliation in `authSlice` picked `ids[0]`, and the strip silently snapped
 * back to the first company of the list. Reported from real use: "I update the
 * company selector, refresh, and the app does not keep the state."
 *
 * That default is not a neutral one. The active company's environment
 * (producción / certificación / sandbox) is what separates a test from a
 * consumed folio, so an unnoticed jump back to another company is the same class
 * of problem as the environment badge showing the wrong thing.
 *
 * ## What is stored, and what it is not
 *
 * Two ids and nothing else. **No credential goes in here**: the session lives in
 * an `HttpOnly` cookie the SPA cannot read, and `auth.apiKey` stays in memory on
 * purpose. Two ids are not an authorisation either — the backend validates
 * `X-Tenant-Id` against the caller's real memberships and answers 403 for
 * anything else, and `authSlice` re-checks both ids against the lists the server
 * returns before they are used. A stale or foreign value therefore degrades to
 * "the first company of the list", never to access to something.
 *
 * `localStorage` and not `sessionStorage`, deliberately: the point is to come
 * back tomorrow, or in a new tab, to the company you were working on. The cost
 * is that two tabs share one preference — each keeps whatever it has in memory,
 * but the one reloaded last inherits the other's choice.
 */

const STORAGE_KEY = 'factit.auth.selection';

/** The ids the identity strip decides. `null` means "not chosen yet". */
export interface PersistedSelection {
  tenantId: string | null;
  companyId: string | null;
}

export const EMPTY_SELECTION: PersistedSelection = {
  tenantId: null,
  companyId: null,
};

/** A stored id, or `null` for anything that is not a non-empty string. */
function readId(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * What was stored, or an empty selection.
 *
 * Never throws and never trusts what it reads. `localStorage` can throw on
 * ACCESS, not merely return `null` — Safari's private browsing with third-party
 * cookies blocked does exactly that — and the value is whatever is sitting in
 * another origin-shared browser profile, so it is parsed defensively: anything
 * that is not an object with string ids reads as "nothing stored".
 */
export function readPersistedSelection(): PersistedSelection {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return EMPTY_SELECTION;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_SELECTION;
    const record = parsed as Record<string, unknown>;
    return {
      tenantId: readId(record.tenantId),
      companyId: readId(record.companyId),
    };
  } catch {
    return EMPTY_SELECTION;
  }
}

/**
 * Stores the selection, or removes the key when there is nothing to remember.
 *
 * Removing on an empty selection is what makes logging out leave nothing behind:
 * `sessionCleared` nulls both ids, and the next person on this browser starts
 * from their own first company instead of inheriting a stranger's choice.
 */
export function writePersistedSelection(selection: PersistedSelection): void {
  try {
    if (selection.tenantId === null && selection.companyId === null) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // No persistence available: the selection lasts as long as the tab does,
    // which is exactly the behaviour this module replaced. Not a reason to
    // break a click.
  }
}
